import { Button, Menu, MenuItem } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import clsx from 'clsx';
import { getAssetList } from 'pages/WellGuardian/WIR/utils/getAssetList';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { updateSelectedAsset, updateSelectedSite } from 'redux/WellGuardian';
import { useCountryState, useSelectedAsset, useSelectedSite } from 'redux/WellGuardian/selectors';
import {
  AssetsIq,
  AssetType,
  COUNTRY_CODE,
  CountryData,
  SitesAg,
  SitesAr,
  SitesBr,
  SitesCg,
  SitesDk,
  SitesIq,
  SitesNg,
  SitesUk,
  SiteType,
} from 'types/wellGuardian';

import styles from './SiteSelector.module.scss';

type SelectorVariant = 'dark' | 'light';

interface SiteSelectorProps {
  variant: SelectorVariant;
  color?: boolean;
}

const SiteSelector: React.FunctionComponent<SiteSelectorProps> = ({ variant, color }) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const countryState = useCountryState();
  const dispatch = useDispatch();

  const ALL_ASSETS_WITH_SITES_LIST = getAssetList(countryState);

  const selectedSite = useSelectedSite();
  const selectedAsset = useSelectedAsset();

  useEffect(() => {
    if (
      countryState === CountryData.iraq &&
      localStorage.getItem(COUNTRY_CODE) === CountryData.iraq
    ) {
      dispatch(updateSelectedSite(SitesIq.RATAWI));
      dispatch(updateSelectedAsset(AssetsIq.RATAWI));
    }
  }, [countryState, dispatch]);

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuCloseSave = (site: string, asset: string) => {
    dispatch(updateSelectedAsset(asset as AssetType));
    dispatch(updateSelectedSite(site as SiteType));
    setAnchorEl(null);
  };

  return (
    <div className={styles.selector} data-testid="site-selector-container">
      <Button
        variant="contained"
        className={clsx(
          color ? styles.selectorButtonColor : styles.selectorButton,
          variant === 'light' && styles.lightButton,
        )}
        disableElevation
        onClick={handleButtonClick}
        endIcon={<KeyboardArrowDownIcon fontSize="medium" className={styles.selectorArrowDown} />}
        data-testid="wg-header-asset-site-selector"
      >
        {selectedAsset && intl.formatMessage({ id: `wellGuardian.assets.${selectedAsset}` })}
        <KeyboardArrowRightIcon fontSize="small" className={styles.selectorArrowRight} />
        {intl.formatMessage({
          id: `wellGuardian.sites.${selectedSite}`,
        })}
      </Button>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: -2,
          horizontal: 32,
        }}
        PaperProps={{
          className: variant === 'light' ? styles.menuPaperLight : styles.menuPaperDark,
        }}
        autoFocus={false}
      >
        <div>
          {ALL_ASSETS_WITH_SITES_LIST.map(item => (
            <SubMenuItem
              key={item.asset}
              variant={variant}
              asset={item.asset}
              subItems={item.sites}
              selectedAsset={selectedAsset === item.asset}
              selectedSite={selectedSite}
              onCloseMainMenuSave={handleMenuCloseSave}
            />
          ))}
        </div>
      </Menu>
    </div>
  );
};

const SubMenuItem: React.FunctionComponent<{
  variant: SelectorVariant;
  asset: string;
  subItems:
    | readonly SitesUk[]
    | readonly SitesDk[]
    | readonly SitesNg[]
    | readonly SitesAg[]
    | readonly SitesCg[]
    | readonly SitesBr[]
    | readonly SitesIq[]
    | readonly SitesAr[];
  selectedAsset: boolean;
  selectedSite: string;
  onCloseMainMenuSave: (site: string, asset: string) => void;
}> = ({ variant, asset, subItems, selectedAsset, selectedSite, onCloseMainMenuSave }) => {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuOpen(!menuOpen);

    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = () => {
    setMenuOpen(false);
  };

  const handleClickSite = (site: string, asset: string) => {
    onCloseMainMenuSave(site, asset);
    handleClose();
  };

  return (
    <>
      <MenuItem
        className={clsx(
          variant === 'light'
            ? menuOpen
              ? styles.subMenuItemLightA
              : selectedAsset
              ? styles.selectedAsset
              : styles.subMenuItemLight
            : menuOpen
            ? styles.subMenuItemDarkA
            : selectedAsset
            ? styles.selectedAsset
            : styles.subMenuItemDark,
        )}
        onClick={handleClick}
        data-testid="wg-header-asset-selector"
      >
        {intl.formatMessage({ id: `wellGuardian.assets.${asset}` })}
        <KeyboardArrowRightIcon />
      </MenuItem>
      <Menu
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={menuOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        PaperProps={{
          className: variant === 'light' ? styles.menuPaperLight : styles.menuPaperDark,
        }}
        autoFocus={false}
      >
        {subItems.map(site => (
          <MenuItem
            key={site}
            className={clsx(
              variant === 'light'
                ? selectedSite === site
                  ? styles.selectedSite
                  : styles.subMenuItemLight
                : selectedSite === site
                ? styles.selectedSite
                : styles.subMenuItemDark,
            )}
            onClick={() => handleClickSite(site, asset)}
            data-testid="wg-header-site-selector"
          >
            {intl.formatMessage({ id: `wellGuardian.sites.${site}` })}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default SiteSelector;
